/**
 * Environment-level page marking for the banner strip.
 *
 * `gcc-cui` renders the always-on CUI banner; `commercial` keeps the
 * per-model confidentiality banner.
 */
export type EnvironmentMarking = 'gcc-cui' | 'commercial'

/**
 * Verbatim CUI banner copy from the approved spec (2026-07-14).
 * Must never be retyped or reformatted; guarded by unit tests.
 */
export const CUI_BANNER_TEXT = 'THIS PAGE CONTAINS DYNAMIC CONTENT AND MAY INCLUDE CUI'

/**
 * Resolves the environment marking from the app's dual-source config.
 *
 * Mirrors the house pattern `ConfigOverrides.FOO ?? import.meta.env.VITE_FOO`:
 * the runtime override wins whenever it is present. The value is normalized
 * (trimmed, lowercased) before comparison.
 *
 * The CUI banner is strictly opt-in: only an explicit `gcc` or `gcc-cui`
 * selects it, and unset, empty, and unrecognized values all resolve to
 * `commercial`, i.e. today's per-model behavior. Every deployment that must
 * carry the CUI marking declares it — the GCC build-time env files
 * (`.env.gcc-staging`, `.env.gcc-production`) do, and a Helm
 * `ConfigOverrides.ENVIRONMENT_MARKING` value can force it at runtime.
 *
 * @param overrideValue - Runtime value from `ConfigOverrides.ENVIRONMENT_MARKING`
 * @param envValue - Build-time value from `import.meta.env.VITE_ENVIRONMENT_MARKING`
 * @returns The resolved marking variant
 */
export function resolveEnvironmentMarking(overrideValue: string | undefined, envValue: string | undefined): EnvironmentMarking {
  const raw = overrideValue ?? envValue
  const normalized = raw?.trim().toLowerCase()
  return normalized === 'gcc' || normalized === 'gcc-cui' ? 'gcc-cui' : 'commercial'
}
